/**
 * InstallationChecklistOverview - Overview of installation checklists for all poles
 */
import React, { useState, useMemo } from 'react';
import { usePoleStore, useCableStore } from '../../stores';
import { useAuthStore } from '../../stores/authStore';
import { useRezimPrace } from '../../store/roleViewStore';
import { Pole } from '../../types';
import { InstallationChecklistDialog } from '../Dialogs/InstallationChecklistDialog';
import { InstallationTechDialog } from '../Dialogs/InstallationTechDialog';
import './InstallationChecklistOverview.css';

type FilterStatus = 'all' | 'not-started' | 'in-progress' | 'completed';

interface ChecklistStats {
  pole: Pole;
  totalTasks: number;
  completedTasks: number;
  percentage: number;
  status: 'not-started' | 'in-progress' | 'completed';
  customTasksCount: number;
  cassetteTasksCount: number;
}

export const InstallationChecklistOverview: React.FC = () => {
  const { poles } = usePoleStore();
  const { cables } = useCableStore();
  const { isAdmin } = useAuthStore();
  const { jePripajanie } = useRezimPrace();
  const [filterStatus, setFilterStatus] = useState<FilterStatus>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPole, setSelectedPole] = useState<Pole | null>(null);

  // Calculate checklist stats for each pole
  const poleStats = useMemo((): ChecklistStats[] => {
    return poles
      .filter(p => !p.is_virtual) // Exclude virtual poles
      .map(pole => {
        const checklist = pole.installation_checklist || {};
        const customTasks = (pole as any).custom_tasks || {};
        const cassette = pole.cassette;

        let completableTasks: string[] = [];
        
        // Get cassette tasks (if cassette exists)
        if (cassette) {
          const taskIds = Object.keys(checklist);
          
          // Filter out tasks that cannot be completed (disconnected ports and ports to other splitters)
          completableTasks = taskIds.filter(taskId => {
            // Check if this is a splitter output task
            const match = taskId.match(/^splitter_(\d+)_output_(\d+)$/);
            if (match) {
              const splitterIndex = parseInt(match[1]);
              const portIndex = parseInt(match[2]);

              const splitter = cassette.splitters?.[splitterIndex];
              if (!splitter) return true;

              const output = splitter.outputs?.[portIndex];

              // Exclude disconnected ports (no cable_id)
              if (!output?.cable_id) {
                return false;
              }

              // Exclude ports connected to other splitters
              if (cassette.splitters) {
                const isConnectedToSplitter = cassette.splitters.some((otherSplitter, otherIndex) => {
                  return otherIndex !== splitterIndex &&
                         otherSplitter.input_source_type === 'splitter_output' &&
                         otherSplitter.input_splitter_index === splitterIndex &&
                         otherSplitter.input_splitter_output === portIndex;
                });

                if (isConnectedToSplitter) {
                  return false;
                }
              }
            }

            return true;
          });
        }

        // Add custom tasks
        const customTaskIds = Object.keys(customTasks);
        const totalTasks = completableTasks.length + customTaskIds.length;

        // Calculate completed tasks
        const completedChecklistCount = completableTasks.filter(id => checklist[id] === true).length;
        const completedCustomTasksCount = customTaskIds.filter(id => customTasks[id].completed === true).length;
        const completedTasks = completedChecklistCount + completedCustomTasksCount;

        const percentage = totalTasks > 0 ? (completedTasks / totalTasks) * 100 : 0;

        let status: 'not-started' | 'in-progress' | 'completed';
        if (totalTasks === 0 || percentage === 0) {
          status = 'not-started';
        } else if (percentage === 100) {
          status = 'completed';
        } else {
          status = 'in-progress';
        }

        return {
          pole,
          totalTasks,
          completedTasks,
          percentage,
          status,
          customTasksCount: customTaskIds.length,
          cassetteTasksCount: completableTasks.length,
        };
      })
      .sort((a, b) => {
        // Sort by name (natural sort)
        const nameA = a.pole.name || '';
        const nameB = b.pole.name || '';
        const partsA = nameA.match(/(\d+|\D+)/g) || [];
        const partsB = nameB.match(/(\d+|\D+)/g) || [];

        for (let i = 0; i < Math.max(partsA.length, partsB.length); i++) {
          const partA = partsA[i] || '';
          const partB = partsB[i] || '';
          const numA = parseInt(partA, 10);
          const numB = parseInt(partB, 10);

          if (!isNaN(numA) && !isNaN(numB)) {
            if (numA !== numB) return numA - numB;
          } else {
            if (partA !== partB) return partA.localeCompare(partB);
          }
        }

        return 0;
      });
  }, [poles]);

  // Filter poles by status and search query
  const filteredStats = useMemo(() => {
    let filtered = poleStats;

    // Filter by status
    if (filterStatus !== 'all') {
      filtered = filtered.filter(stat => stat.status === filterStatus);
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(stat =>
        stat.pole.name?.toLowerCase().includes(query) ||
        stat.pole.pole_type?.toLowerCase().includes(query) ||
        stat.pole.notes?.toLowerCase().includes(query)
      );
    }

    return filtered;
  }, [poleStats, filterStatus, searchQuery]);

  // Calculate summary stats
  const summaryStats = useMemo(() => {
    const total = poleStats.length;
    const notStarted = poleStats.filter(s => s.status === 'not-started').length;
    const inProgress = poleStats.filter(s => s.status === 'in-progress').length;
    const completed = poleStats.filter(s => s.status === 'completed').length;
    const totalTasks = poleStats.reduce((sum, s) => sum + s.totalTasks, 0);
    const completedTasks = poleStats.reduce((sum, s) => sum + s.completedTasks, 0);
    const overallPercentage = totalTasks > 0 ? (completedTasks / totalTasks) * 100 : 0;

    return {
      total,
      notStarted,
      inProgress,
      completed,
      totalTasks,
      completedTasks,
      overallPercentage,
    };
  }, [poleStats]);

  const handleOpenChecklist = (pole: Pole) => {
    setSelectedPole(pole);
  };

  const handleCloseChecklist = () => {
    setSelectedPole(null);
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'completed':
        return 'status-badge-completed';
      case 'in-progress':
        return 'status-badge-in-progress';
      case 'not-started':
        return 'status-badge-not-started';
      default:
        return '';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'completed':
        return 'Splnené';
      case 'in-progress':
        return 'Čiastočne';
      case 'not-started':
        return 'Nesplnené';
      default:
        return '';
    }
  };

  return (
    <div className="installation-checklist-overview">
      {/* Header */}
      <div className="overview-header">
        <h1>📋 Installation Checklist - Prehľad</h1>
        <p>Prehľad inštalačných checklistov pre všetky stĺpiky</p>
      </div>

      {/* Summary Stats */}
      <div className="summary-stats">
        <div className="stat-card stat-total">
          <div className="stat-icon">📊</div>
          <div className="stat-content">
            <div className="stat-label">Celkom stĺpikov</div>
            <div className="stat-value">{summaryStats.total}</div>
          </div>
        </div>

        <div className="stat-card stat-not-started">
          <div className="stat-icon">⚪</div>
          <div className="stat-content">
            <div className="stat-label">Nesplnené</div>
            <div className="stat-value">{summaryStats.notStarted}</div>
          </div>
        </div>

        <div className="stat-card stat-in-progress">
          <div className="stat-icon">🟠</div>
          <div className="stat-content">
            <div className="stat-label">Čiastočne splnené</div>
            <div className="stat-value">{summaryStats.inProgress}</div>
          </div>
        </div>

        <div className="stat-card stat-completed">
          <div className="stat-icon">🟢</div>
          <div className="stat-content">
            <div className="stat-label">Splnené</div>
            <div className="stat-value">{summaryStats.completed}</div>
          </div>
        </div>

        <div className="stat-card stat-tasks">
          <div className="stat-icon">✅</div>
          <div className="stat-content">
            <div className="stat-label">Úlohy</div>
            <div className="stat-value">
              {summaryStats.completedTasks} / {summaryStats.totalTasks}
            </div>
            <div className="stat-percentage">
              {summaryStats.overallPercentage.toFixed(1)}%
            </div>
          </div>
        </div>
      </div>

      {/* Overall Progress Bar */}
      <div className="overall-progress">
        <div className="progress-header">
          <span className="progress-label">Celkový pokrok</span>
          <span className="progress-percentage">
            {summaryStats.overallPercentage.toFixed(1)}%
          </span>
        </div>
        <div className="progress-bar-container">
          <div
            className="progress-bar-fill"
            style={{ width: `${summaryStats.overallPercentage}%` }}
          />
        </div>
      </div>

      {/* Filters */}
      <div className="filters-section">
        <div className="filter-buttons">
          <button
            className={filterStatus === 'all' ? 'filter-btn active' : 'filter-btn'}
            onClick={() => setFilterStatus('all')}
          >
            Všetky ({poleStats.length})
          </button>
          <button
            className={filterStatus === 'not-started' ? 'filter-btn active' : 'filter-btn'}
            onClick={() => setFilterStatus('not-started')}
          >
            ⚪ Nesplnené ({summaryStats.notStarted})
          </button>
          <button
            className={filterStatus === 'in-progress' ? 'filter-btn active' : 'filter-btn'}
            onClick={() => setFilterStatus('in-progress')}
          >
            🟠 Čiastočne ({summaryStats.inProgress})
          </button>
          <button
            className={filterStatus === 'completed' ? 'filter-btn active' : 'filter-btn'}
            onClick={() => setFilterStatus('completed')}
          >
            🟢 Splnené ({summaryStats.completed})
          </button>
        </div>

        <div className="search-container">
          <input
            type="text"
            placeholder="🔍 Hľadať stĺpik..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
          {searchQuery && (
            <button
              className="search-clear"
              onClick={() => setSearchQuery('')}
              title="Vymazať"
            >
              ✕
            </button>
          )}
        </div>
      </div>

      {/* Results count */}
      <div className="results-count">
        Zobrazené: {filteredStats.length} / {poleStats.length} stĺpikov
      </div>

      {/* Poles Table */}
      <div className="poles-table-container">
        <table className="poles-table">
          <thead>
            <tr>
              <th>Stĺpik</th>
              <th>Typ</th>
              <th>Stav</th>
              <th>Pokrok</th>
              <th>Úlohy</th>
              <th>Custom Tasks</th>
              <th>Akcie</th>
            </tr>
          </thead>
          <tbody>
            {filteredStats.map(stat => (
              <tr key={stat.pole.id}>
                <td className="pole-name">{stat.pole.name}</td>
                <td>
                  <span className={`pole-type-badge ${stat.pole.pole_type}`}>
                    {stat.pole.pole_type}
                  </span>
                </td>
                <td>
                  <span className={`status-badge ${getStatusBadgeClass(stat.status)}`}>
                    {getStatusLabel(stat.status)}
                  </span>
                </td>
                <td className="progress-cell">
                  <div className="progress-bar-small-container">
                    <div
                      className={`progress-bar-small-fill ${stat.status}`}
                      style={{ width: `${stat.percentage}%` }}
                    />
                  </div>
                  <span className="progress-text">{stat.percentage.toFixed(0)}%</span>
                </td>
                <td className="tasks-count">
                  {stat.completedTasks} / {stat.totalTasks}
                </td>
                <td className="custom-tasks-count">
                  {stat.customTasksCount > 0 ? (
                    <span className="custom-tasks-badge">{stat.customTasksCount}</span>
                  ) : (
                    <span className="no-custom-tasks">-</span>
                  )}
                </td>
                <td>
                  <button
                    className="btn-open-checklist"
                    onClick={() => handleOpenChecklist(stat.pole)}
                  >
                    Otvoriť
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {filteredStats.length === 0 && (
          <div className="no-results">
            <p>Žiadne stĺpiky nenájdené</p>
          </div>
        )}
      </div>

      {/* Installation Checklist Dialog - Switch based on role view mode */}
      {selectedPole && (
        <>
          {jePripajanie ? (
            <InstallationTechDialog
              pole={selectedPole}
              projectId={selectedPole.project_id}
              onClose={handleCloseChecklist}
            />
          ) : (
            <InstallationChecklistDialog
              pole={selectedPole}
              onClose={handleCloseChecklist}
            />
          )}
        </>
      )}
    </div>
  );
};

